import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, statusConfig, priorityConfig, formatDate, formatRelativeTime, nowChile, utcToChileInput, chileInputToUTC } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  Clock,
  User,
  Calendar,
  AlertTriangle,
  CheckCircle,
  MessageSquare,
  ArrowRightLeft,
  Loader2,
  Target,
  History,
  TimerReset,
  Package,
  XCircle,
  TrendingUp,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RichTextView } from '../components/RichTextEditor';
import RichTextEditor from '../components/RichTextEditor';
import { Pencil } from 'lucide-react';
import { Slider } from '../components/ui/slider';

export default function TicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isSolucionador, isJefatura } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [solucionadores, setSolucionadores] = useState([]);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [extensionDialogOpen, setExtensionDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [comment, setComment] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [extensions, setExtensions] = useState([]);
  const [extensionType, setExtensionType] = useState('tiempo');
  const [extensionReason, setExtensionReason] = useState('');
  const [extensionHours, setExtensionHours] = useState('');
  const [extensionResource, setExtensionResource] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', description: '', priority: 'media', category_id: '', gerencia_area_id: '', due_date: '' });
  const [categories, setCategories] = useState([]);
  const [gerencias, setGerencias] = useState([]);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [progressValue, setProgressValue] = useState(0);
  const [progressComment, setProgressComment] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const fetchTicket = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/tickets/${id}`);
      setTicket(response.data);
    } catch (error) {
      toast.error('Error al cargar ticket');
      navigate('/tickets');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  const fetchGerencias = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/gerencias`);
      setGerencias(response.data);
    } catch (error) {
      console.error('Error fetching gerencias:', error);
    }
  }, []);

  const fetchSolucionadores = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/users/solucionadores`);
      setSolucionadores(response.data);
    } catch (error) {
      console.error('Error fetching solucionadores:', error);
    }
  }, []);

  const fetchExtensions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/tickets/${id}/extensions`);
      setExtensions(response.data);
    } catch (error) {
      console.error('Error fetching extensions:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchTicket();
    fetchExtensions();
    fetchCategories();
    fetchGerencias();
    if (isSolucionador || isJefatura) {
      fetchSolucionadores();
    }
  }, [id, isSolucionador, isJefatura, fetchTicket, fetchExtensions, fetchSolucionadores, fetchCategories, fetchGerencias]);

  const handleExtensionRequest = async () => {
    setActionLoading(true);
    try {
      const payload = {
        request_type: extensionType,
        reason: extensionReason,
      };
      if (extensionType === 'tiempo') {
        payload.requested_hours = parseInt(extensionHours) || 0;
      } else {
        payload.resource_description = extensionResource;
      }
      await axios.post(`${API_URL}/tickets/${id}/extensions`, payload);
      toast.success('Solicitud enviada correctamente');
      setExtensionDialogOpen(false);
      setExtensionReason('');
      setExtensionHours('');
      setExtensionResource('');
      fetchExtensions();
      fetchTicket();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar solicitud');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = async () => {
    setActionLoading(true);
    try {
      await axios.patch(`${API_URL}/tickets/${id}/status`, {
        status: selectedStatus,
        comment: comment || null
      });
      toast.success('Estado actualizado');
      setStatusDialogOpen(false);
      setComment('');
      fetchTicket();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar estado');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssign = async () => {
    setActionLoading(true);
    try {
      await axios.patch(`${API_URL}/tickets/${id}/assign`, {
        assignee_id: selectedAssignee,
        comment: comment || null
      });
      toast.success('Ticket asignado');
      setAssignDialogOpen(false);
      setComment('');
      fetchTicket();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar ticket');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnassign = async () => {
    setActionLoading(true);
    try {
      await axios.patch(`${API_URL}/tickets/${id}/assign`, {
        assignee_id: null,
        comment: comment || null
      });
      toast.success('Ticket sin asignación');
      setAssignDialogOpen(false);
      setComment('');
      setSelectedAssignee('');
      fetchTicket();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al quitar asignación');
    } finally {
      setActionLoading(false);
    }
  };

  const handleComment = async () => {
    if (!comment.trim()) return;
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/tickets/${id}/comment?comment=${encodeURIComponent(comment)}`);
      toast.success('Comentario agregado');
      setCommentDialogOpen(false);
      setComment('');
      fetchTicket();
    } catch (error) {
      toast.error('Error al agregar comentario');
    } finally {
      setActionLoading(false);
    }
  };

  const openProgressDialog = () => {
    setProgressValue(ticket?.progress || 0);
    setProgressComment('');
    setProgressDialogOpen(true);
  };

  const handleProgressReport = async () => {
    if (!progressComment.trim()) {
      toast.error('Describe brevemente el avance realizado');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/tickets/${id}/progress`, {
        progress: progressValue,
        comment: progressComment.trim(),
      });
      toast.success(`Avance reportado: ${progressValue}%`);
      setProgressDialogOpen(false);
      setProgressComment('');
      fetchTicket();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reportar avance');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await axios.delete(`${API_URL}/tickets/${id}`);
      toast.success('Ticket eliminado');
      setDeleteDialogOpen(false);
      navigate('/tickets');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar ticket');
      setActionLoading(false);
    }
  };

  const openEditDialog = () => {
    if (!ticket) return;
    setEditForm({
      title: ticket.title || '',
      description: ticket.description || '',
      priority: ticket.priority || 'media',
      category_id: ticket.category_id || '',
      gerencia_area_id: ticket.gerencia_area_id || '',
      due_date: utcToChileInput(ticket.due_date),
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editForm.title.trim()) {
      toast.error('El título es obligatorio');
      return;
    }
    if (!editForm.gerencia_area_id) {
      toast.error('Selecciona una gerencia/área');
      return;
    }
    setActionLoading(true);
    try {
      const payload = {
        title: editForm.title.trim(),
        description: editForm.description,
        priority: editForm.priority,
        category_id: editForm.category_id || null,
        gerencia_area_id: editForm.gerencia_area_id,
        due_date: chileInputToUTC(editForm.due_date),
      };
      await axios.patch(`${API_URL}/tickets/${id}`, payload);
      toast.success('Ticket actualizado');
      setEditDialogOpen(false);
      fetchTicket();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar ticket');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#7FD02E]" />
      </div>
    );
  }

  if (!ticket) {
    return null;
  }

  const isSLABreached = ticket.sla_resolution_deadline && new Date(ticket.sla_resolution_deadline) < nowChile();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ticket-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/tickets')} className="text-gray-600" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-lg text-[#7FD02E]">{ticket.ticket_number}</span>
            <Badge className={statusConfig[ticket.status]?.color}>
              {statusConfig[ticket.status]?.label}
            </Badge>
            <Badge className={priorityConfig[ticket.priority]?.color}>
              {priorityConfig[ticket.priority]?.label}
            </Badge>
            {isSLABreached && (
              <Badge className="bg-rose-100 text-rose-700 border-rose-200">
                <AlertTriangle className="w-3 h-3 mr-1" />
                SLA Vencido
              </Badge>
            )}
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight mt-2 text-gray-800">{ticket.title}</h1>
        </div>
        {(isJefatura || ticket.creator_id === user?.id) && ticket.status !== 'cerrado' && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={openEditDialog}
              className="border-gray-300"
              data-testid="edit-ticket-btn"
            >
              <Pencil className="w-4 h-4 mr-2" />
              Editar
            </Button>
            {isJefatura && (
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(true)}
                className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                data-testid="delete-ticket-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Assignee highlight banner */}
      <div className={`rounded-xl border-2 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 ${
        ticket.assignee
          ? 'border-[#7FD02E]/50 bg-gradient-to-r from-[#7FD02E]/10 to-emerald-50'
          : 'border-amber-200 bg-amber-50/40'
      }`}>
        <div className="flex items-center gap-4">
          {ticket.assignee ? (
            <>
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#7FD02E] to-emerald-500 flex items-center justify-center text-white text-xl font-bold shadow-md">
                {ticket.assignee.first_name?.charAt(0)}{ticket.assignee.last_name?.charAt(0)}
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-[#5BA51E] font-semibold">
                  Asignado a
                </p>
                <p className="text-lg font-bold text-gray-900 leading-tight">
                  {ticket.assignee.first_name} {ticket.assignee.last_name}
                </p>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                  <Badge variant="outline" className="text-xs">
                    {ticket.assignee.role === 'jefatura' ? 'Jefatura' : 'Solucionador'}
                  </Badge>
                  {ticket.reassignment_count > 0 && (
                    <span>· {ticket.reassignment_count} reasignacion{ticket.reassignment_count > 1 ? 'es' : ''}</span>
                  )}
                </div>
                {/* Progress bar */}
                <div className="mt-2 w-64">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-600 font-medium">Avance</span>
                    <span className={`font-bold ${
                      ticket.progress >= 100 ? 'text-emerald-600' :
                      ticket.progress >= 50 ? 'text-[#5BA51E]' :
                      'text-amber-600'
                    }`} data-testid="progress-percent">
                      {ticket.progress || 0}%
                    </span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${
                        ticket.progress >= 100 ? 'bg-emerald-500' :
                        ticket.progress >= 50 ? 'bg-[#7FD02E]' :
                        'bg-amber-400'
                      }`}
                      style={{ width: `${ticket.progress || 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 shadow-sm">
                <User className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-amber-700 font-semibold">
                  Sin asignar
                </p>
                <p className="text-lg font-bold text-gray-900 leading-tight">
                  Pendiente de asignación
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Espera que un jefatura asigne un solucionador
                </p>
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-6">
          {ticket.due_date && (
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold flex items-center gap-1 justify-end">
                <Clock className="w-3 h-3" /> Fecha límite
              </p>
              <p className={`text-base font-bold leading-tight ${
                new Date(ticket.due_date) < new Date() && !['resuelto', 'cerrado'].includes(ticket.status)
                  ? 'text-rose-600'
                  : 'text-gray-900'
              }`}>
                {formatDate(ticket.due_date)}
              </p>
              <p className="text-xs text-gray-500">
                {(() => {
                  const diff = Math.ceil((new Date(ticket.due_date) - new Date()) / (1000 * 60 * 60 * 24));
                  if (['resuelto', 'cerrado'].includes(ticket.status)) return 'cerrado';
                  if (diff < 0) return `vencido hace ${Math.abs(diff)} día${Math.abs(diff) !== 1 ? 's' : ''}`;
                  if (diff === 0) return 'vence hoy';
                  return `en ${diff} día${diff !== 1 ? 's' : ''}`;
                })()}
              </p>
            </div>
          )}
          {isJefatura && ticket.status !== 'cerrado' && (
            <Button
              onClick={() => setAssignDialogOpen(true)}
              className={ticket.assignee_id
                ? 'bg-white border-2 border-[#7FD02E] text-[#5BA51E] hover:bg-[#7FD02E]/10'
                : 'bg-[#7FD02E] hover:bg-[#6BB825] text-white'
              }
              data-testid="assign-banner-btn"
            >
              <User className="w-4 h-4 mr-2" />
              {ticket.assignee_id ? 'Reasignar' : 'Asignar'}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card className="border-gray-200 bg-white shadow-sm" data-testid="ticket-description-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-gray-800">Descripción</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-[200px] overflow-auto">
                <RichTextView html={ticket.description} className="text-gray-600" />
              </div>
            </CardContent>
          </Card>

          {/* Avances reportados (visible para todos) */}
          {(() => {
            const progressUpdates = (ticket.history || []).filter(h => h.action === 'progress_update');
            if (progressUpdates.length === 0) return null;
            return (
              <Card className="border-gray-200 bg-white shadow-sm" data-testid="ticket-progress-updates-card">
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-lg flex items-center gap-2 text-gray-800">
                    <TrendingUp className="w-5 h-5 text-[#7FD02E]" />
                    Avances reportados ({progressUpdates.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[260px] overflow-auto pr-2">
                    {progressUpdates.map((upd) => {
                      // parse "Avance: X% → Y%. <comment>"
                      const match = upd.comment?.match(/^Avance:\s*(\d+)%\s*→\s*(\d+)%\.\s*([\s\S]*)$/);
                      const from = match ? parseInt(match[1]) : null;
                      const to = match ? parseInt(match[2]) : null;
                      const note = match ? match[3].trim() : (upd.comment || '');
                      const delta = from !== null && to !== null ? to - from : 0;
                      const toColor = to === null ? 'text-gray-700'
                        : to >= 100 ? 'text-emerald-600'
                        : to >= 50 ? 'text-[#5BA51E]'
                        : 'text-amber-600';
                      return (
                        <div
                          key={upd.id}
                          className="p-3 rounded-lg border border-gray-100 bg-gradient-to-r from-[#7FD02E]/5 to-transparent"
                          data-testid={`progress-update-${upd.id}`}
                        >
                          <div className="flex items-center gap-3 flex-wrap mb-2">
                            {to !== null && (
                              <span className={`text-xl font-bold ${toColor}`}>{to}%</span>
                            )}
                            {from !== null && (
                              <span className="text-xs text-gray-400">desde {from}%</span>
                            )}
                            {delta > 0 && (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
                                +{delta} pts
                              </Badge>
                            )}
                            <span className="text-xs text-gray-400 ml-auto">
                              {formatRelativeTime(upd.created_at)} · {formatDate(upd.created_at)}
                            </span>
                          </div>
                          {to !== null && (
                            <div className="h-1.5 w-full rounded-full bg-gray-200 overflow-hidden mb-2">
                              <div
                                className={`h-full ${
                                  to >= 100 ? 'bg-emerald-500' :
                                  to >= 50 ? 'bg-[#7FD02E]' :
                                  'bg-amber-400'
                                }`}
                                style={{ width: `${to}%` }}
                              />
                            </div>
                          )}
                          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                            {note || <span className="text-gray-400 italic">Sin comentario</span>}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Actions */}
          {isSolucionador && ticket.status !== 'cerrado' && (
            <Card className="border-gray-200 bg-white shadow-sm" data-testid="ticket-actions-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-gray-800">Acciones</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {/* Report Progress - only if I'm the assignee */}
                  {ticket.assignee_id === user?.id && (
                    <Button
                      onClick={openProgressDialog}
                      className="bg-[#7FD02E] hover:bg-[#6BB825] text-white"
                      data-testid="report-progress-btn"
                    >
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Reportar Avance
                    </Button>
                  )}

                  {/* Change Status */}
                  <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="border-gray-200 text-gray-700 hover:bg-gray-50" data-testid="change-status-btn">
                        <ArrowRightLeft className="w-4 h-4 mr-2" />
                        Cambiar Estado
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Cambiar Estado del Ticket</DialogTitle>
                        <DialogDescription>
                          Selecciona el nuevo estado para este ticket
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                          <SelectTrigger data-testid="status-select">
                            <SelectValue placeholder="Seleccionar estado" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(statusConfig).map(([key, { label }]) => (
                              <SelectItem key={key} value={key}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Textarea
                          placeholder="Comentario opcional..."
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          data-testid="status-comment-input"
                        />
                      </div>
                      <DialogFooter>
                        <Button variant="ghost" onClick={() => setStatusDialogOpen(false)}>
                          Cancelar
                        </Button>
                        <Button 
                          onClick={handleStatusChange} 
                          disabled={!selectedStatus || actionLoading}
                          data-testid="confirm-status-btn"
                        >
                          {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                          Confirmar
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  {/* Assign / Reassign - dialog controlled from banner button */}
                  {isJefatura && (
                    <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{ticket.assignee_id ? 'Reasignar Ticket' : 'Asignar Ticket'}</DialogTitle>
                          <DialogDescription>
                            {ticket.assignee_id
                              ? `Actualmente asignado a ${ticket.assignee?.first_name} ${ticket.assignee?.last_name}. Selecciona el nuevo solucionador.`
                              : 'Selecciona un solucionador para este ticket'}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
                            <SelectTrigger data-testid="assignee-select">
                              <SelectValue placeholder="Seleccionar solucionador" />
                            </SelectTrigger>
                            <SelectContent>
                              {solucionadores
                                .filter((sol) => sol.id !== ticket.assignee_id)
                                .map((sol) => (
                                  <SelectItem key={sol.id} value={sol.id}>
                                    {sol.first_name} {sol.last_name}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                          <Textarea
                            placeholder="Comentario opcional..."
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            data-testid="assign-comment-input"
                          />
                        </div>
                        <DialogFooter className="gap-2 sm:gap-2">
                          {ticket.assignee_id && (
                            <Button
                              variant="outline"
                              onClick={handleUnassign}
                              disabled={actionLoading}
                              className="border-rose-200 text-rose-700 hover:bg-rose-50 mr-auto"
                              data-testid="unassign-btn"
                            >
                              {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                              Quitar asignación
                            </Button>
                          )}
                          <Button variant="ghost" onClick={() => setAssignDialogOpen(false)}>
                            Cancelar
                          </Button>
                          <Button 
                            onClick={handleAssign} 
                            disabled={!selectedAssignee || actionLoading}
                            data-testid="confirm-assign-btn"
                          >
                            {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            {ticket.assignee_id ? 'Reasignar' : 'Asignar'}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}

                  {/* Add Comment */}
                  <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="border-gray-200 text-gray-700 hover:bg-gray-50" data-testid="add-comment-btn">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Comentar
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Agregar Comentario</DialogTitle>
                      </DialogHeader>
                      <div className="py-4">
                        <Textarea
                          placeholder="Escribe tu comentario..."
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          rows={4}
                          data-testid="comment-input"
                        />
                      </div>
                      <DialogFooter>
                        <Button variant="ghost" onClick={() => setCommentDialogOpen(false)}>
                          Cancelar
                        </Button>
                        <Button 
                          onClick={handleComment} 
                          disabled={!comment.trim() || actionLoading}
                          data-testid="confirm-comment-btn"
                        >
                          {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                          Agregar
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  {/* Extension / Resource Request */}
                  {ticket.assignee_id === user?.id && (
                    <Dialog open={extensionDialogOpen} onOpenChange={setExtensionDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="request-extension-btn">
                          <TimerReset className="w-4 h-4 mr-2" />
                          Solicitar Extensión
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[500px]">
                        <DialogHeader>
                          <DialogTitle>Nueva Solicitud</DialogTitle>
                          <DialogDescription>
                            Solicita más tiempo o recursos para este ticket
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label>Tipo de solicitud</Label>
                            <Select value={extensionType} onValueChange={setExtensionType}>
                              <SelectTrigger data-testid="extension-type-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="tiempo">Extensión de Tiempo</SelectItem>
                                <SelectItem value="recurso">Solicitud de Recursos</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {extensionType === 'tiempo' && (
                            <div className="space-y-2">
                              <Label>Horas adicionales solicitadas</Label>
                              <Input
                                type="number"
                                min="1"
                                placeholder="Ej: 8"
                                value={extensionHours}
                                onChange={(e) => setExtensionHours(e.target.value)}
                                data-testid="extension-hours-input"
                              />
                            </div>
                          )}

                          {extensionType === 'recurso' && (
                            <div className="space-y-2">
                              <Label>Descripción de recursos necesarios</Label>
                              <Textarea
                                placeholder="Describe los recursos que necesitas..."
                                value={extensionResource}
                                onChange={(e) => setExtensionResource(e.target.value)}
                                rows={3}
                                data-testid="extension-resource-input"
                              />
                            </div>
                          )}

                          <div className="space-y-2">
                            <Label>Motivo de la solicitud</Label>
                            <Textarea
                              placeholder="Explica por qué necesitas esta extensión..."
                              value={extensionReason}
                              onChange={(e) => setExtensionReason(e.target.value)}
                              rows={3}
                              data-testid="extension-reason-input"
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="ghost" onClick={() => setExtensionDialogOpen(false)}>
                            Cancelar
                          </Button>
                          <Button
                            onClick={handleExtensionRequest}
                            disabled={
                              !extensionReason.trim() ||
                              (extensionType === 'tiempo' && !extensionHours) ||
                              (extensionType === 'recurso' && !extensionResource.trim()) ||
                              actionLoading
                            }
                            className="bg-[#7FD02E] hover:bg-[#6AB825] text-white"
                            data-testid="confirm-extension-btn"
                          >
                            {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            Enviar Solicitud
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Extension Requests */}
          {extensions.length > 0 && (
            <Card className="border-gray-200 bg-white shadow-sm" data-testid="ticket-extensions-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2 text-gray-800">
                  <TimerReset className="w-5 h-5 text-blue-500" />
                  Solicitudes ({extensions.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {extensions.map((ext) => {
                    const statusColors = {
                      pendiente: 'bg-amber-100 text-amber-700 border-amber-200',
                      aprobada: 'bg-emerald-100 text-emerald-700 border-emerald-200',
                      rechazada: 'bg-rose-100 text-rose-700 border-rose-200',
                    };
                    const statusLabels = { pendiente: 'Pendiente', aprobada: 'Aprobada', rechazada: 'Rechazada' };
                    return (
                      <div key={ext.id} className="p-3 rounded-lg border border-gray-100 bg-gray-50/50 space-y-2" data-testid={`extension-item-${ext.id}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={ext.request_type === 'tiempo' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-purple-100 text-purple-700 border-purple-200'}>
                            {ext.request_type === 'tiempo' ? (
                              <><Clock className="w-3 h-3 mr-1" />Tiempo</>
                            ) : (
                              <><Package className="w-3 h-3 mr-1" />Recursos</>
                            )}
                          </Badge>
                          <Badge className={statusColors[ext.status]}>
                            {statusLabels[ext.status]}
                          </Badge>
                          {ext.requested_hours && (
                            <span className="text-xs text-gray-500">{ext.requested_hours}h solicitadas</span>
                          )}
                          <span className="text-xs text-gray-400 ml-auto">{formatRelativeTime(ext.created_at)}</span>
                        </div>
                        <p className="text-sm text-gray-600">{ext.reason}</p>
                        {ext.resource_description && (
                          <p className="text-sm text-gray-500"><Package className="w-3 h-3 inline mr-1" />{ext.resource_description}</p>
                        )}
                        {ext.reviewer_comment && (
                          <div className="p-2 rounded bg-white border border-gray-100">
                            <p className="text-xs text-gray-500">
                              <strong>{ext.reviewer?.first_name}:</strong> {ext.reviewer_comment}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* History */}
          <Card className="border-gray-200 bg-white shadow-sm" data-testid="ticket-history-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2 text-gray-800">
                <History className="w-5 h-5 text-[#4A4A4A]" />
                Historial
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-h-[280px] overflow-auto pr-2">
                {ticket.history?.length > 0 ? (
                  ticket.history.map((entry, index) => (
                    <div 
                      key={entry.id} 
                      className="relative pl-6 pb-4 border-l border-gray-200 last:border-transparent"
                    >
                      <div className="absolute -left-1.5 top-0 w-3 h-3 rounded-full bg-[#7FD02E]" />
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-sm text-gray-800">
                            {entry.action === 'created' && 'Ticket creado'}
                            {entry.action === 'status_change' && (
                              <>Estado: {statusConfig[entry.from_status]?.label || '-'} → {statusConfig[entry.to_status]?.label}</>
                            )}
                            {entry.action === 'assignment' && 'Ticket asignado'}
                            {entry.action === 'comment' && 'Comentario agregado'}
                            {entry.action === 'progress_update' && (
                              <span className="flex items-center gap-1">
                                <TrendingUp className="w-3.5 h-3.5 text-[#7FD02E]" />
                                Avance reportado
                              </span>
                            )}
                            {entry.action === 'extension_request' && 'Solicitud creada'}
                            {entry.action === 'extension_review' && 'Solicitud revisada'}
                          </p>
                          {entry.comment && (
                            <p className="text-sm text-gray-500 mt-1">{entry.comment}</p>
                          )}
                          {entry.time_in_status_minutes && (
                            <p className="text-xs text-gray-400 mt-1">
                              Tiempo en estado anterior: {Math.round(entry.time_in_status_minutes / 60)}h {entry.time_in_status_minutes % 60}m
                            </p>
                          )}
                        </div>
                        <span className="text-xs text-gray-400 whitespace-nowrap ml-4">
                          {formatRelativeTime(entry.created_at)}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-400 text-center py-4">Sin historial</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Info consolidado */}
          <Card className="border-gray-200 bg-white shadow-sm">
            <CardContent className="p-4 space-y-3">
              {/* Creador */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Creador</span>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-[#7FD02E]/20 flex items-center justify-center">
                    <User className="w-3 h-3 text-[#7FD02E]" />
                  </div>
                  <span className="text-gray-700">
                    {ticket.creator?.first_name} {ticket.creator?.last_name}
                  </span>
                </div>
              </div>

              {ticket.gerencia_area && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Gerencia/Área</span>
                  <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200">
                    {ticket.gerencia_area.name}
                  </Badge>
                </div>
              )}

              {ticket.category && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Categoría</span>
                  <div className="flex items-center gap-2">
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: ticket.category.color }}
                    />
                    <span className="text-gray-700">{ticket.category.name}</span>
                  </div>
                </div>
              )}

              <div className="border-t border-gray-100 pt-3 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Creado</span>
                  <span className="font-mono text-gray-700">{formatDate(ticket.created_at)}</span>
                </div>
                {ticket.first_response_at && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">1ª respuesta</span>
                    <span className="font-mono text-gray-700">{formatDate(ticket.first_response_at)}</span>
                  </div>
                )}
                {ticket.resolved_at && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Resuelto</span>
                    <span className="font-mono text-gray-700">{formatDate(ticket.resolved_at)}</span>
                  </div>
                )}
                {ticket.closed_at && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Cerrado</span>
                    <span className="font-mono text-gray-700">{formatDate(ticket.closed_at)}</span>
                  </div>
                )}
                {ticket.total_time_minutes > 0 && (
                  <div className="flex items-center justify-between text-xs pt-1 border-t border-gray-100 mt-1">
                    <span className="text-gray-500">Tiempo total</span>
                    <span className="font-bold text-[#7FD02E]">
                      {Math.floor(ticket.total_time_minutes / 60)}h {ticket.total_time_minutes % 60}m
                    </span>
                  </div>
                )}
              </div>

              {(ticket.sla_response_deadline || ticket.sla_resolution_deadline) && (
                <div className="border-t border-gray-100 pt-3 space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500 flex items-center gap-1"><Target className="w-3 h-3 text-[#7FD02E]" /> SLA</span>
                    {ticket.sla_breached ? (
                      <Badge className="bg-rose-100 text-rose-700 text-xs">Incumplido</Badge>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">En tiempo</Badge>
                    )}
                  </div>
                  {ticket.sla_response_deadline && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">Respuesta</span>
                      <span className="font-mono text-gray-700">{formatDate(ticket.sla_response_deadline)}</span>
                    </div>
                  )}
                  {ticket.sla_resolution_deadline && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">Resolución</span>
                      <span className={`font-mono ${isSLABreached ? 'text-rose-600' : 'text-gray-700'}`}>
                        {formatDate(ticket.sla_resolution_deadline)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="w-5 h-5" />
              Eliminar ticket
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2">
              <span className="block">
                Vas a eliminar <code className="bg-gray-100 px-1.5 py-0.5 rounded text-rose-700">{ticket?.ticket_number}</code> · {ticket?.title}.
              </span>
              <span className="block text-rose-600 font-medium">
                Esta acción es irreversible y borrará el historial, comentarios, asignaciones, solicitudes de extensión y adjuntos relacionados.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              disabled={actionLoading}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="confirm-delete-btn"
            >
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Progress Report Dialog */}
      <Dialog open={progressDialogOpen} onOpenChange={setProgressDialogOpen}>
        <DialogContent className="bg-white sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#7FD02E]" />
              Reportar avance
            </DialogTitle>
            <DialogDescription>
              Desliza para indicar el porcentaje completado y deja un registro escrito para la jefatura.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-2">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-gray-700">Progreso</Label>
                <span className={`text-2xl font-bold ${
                  progressValue >= 100 ? 'text-emerald-600' :
                  progressValue >= 50 ? 'text-[#5BA51E]' :
                  'text-amber-600'
                }`} data-testid="progress-slider-value">
                  {progressValue}%
                </span>
              </div>
              <Slider
                value={[progressValue]}
                onValueChange={(v) => setProgressValue(v[0])}
                max={100}
                step={5}
                className="w-full"
                data-testid="progress-slider"
              />
              <div className="flex justify-between text-xs text-gray-400 px-1">
                <span>0%</span>
                <span>25%</span>
                <span>50%</span>
                <span>75%</span>
                <span>100%</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="progress-comment" className="text-gray-700">
                Registro escrito *
              </Label>
              <Textarea
                id="progress-comment"
                placeholder="Describe el avance: qué se completó, próximos pasos, bloqueos..."
                value={progressComment}
                onChange={(e) => setProgressComment(e.target.value)}
                rows={4}
                data-testid="progress-comment-input"
              />
              <p className="text-xs text-gray-400">La jefatura verá este registro en el historial en tiempo real.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setProgressDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleProgressReport}
              disabled={!progressComment.trim() || actionLoading}
              className="bg-[#7FD02E] hover:bg-[#6BB825] text-white"
              data-testid="confirm-progress-btn"
            >
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Guardar avance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Ticket Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar ticket</DialogTitle>
            <DialogDescription>Modifica los campos del ticket {ticket.code}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="edit-title">Título *</Label>
              <Input
                id="edit-title"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                data-testid="edit-title-input"
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <RichTextEditor
                value={editForm.description}
                onChange={(html) => setEditForm({ ...editForm, description: html })}
                placeholder="Describe el ticket..."
                minHeight={140}
                data-testid="edit-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prioridad</Label>
                <Select
                  value={editForm.priority}
                  onValueChange={(v) => setEditForm({ ...editForm, priority: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baja">Baja</SelectItem>
                    <SelectItem value="media">Media</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="critica">Crítica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categoría</Label>
                <Select
                  value={editForm.category_id || 'none'}
                  onValueChange={(v) => setEditForm({ ...editForm, category_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger><SelectValue placeholder="Sin categoría" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin categoría</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Gerencia / Área *</Label>
                <Select
                  value={editForm.gerencia_area_id}
                  onValueChange={(v) => setEditForm({ ...editForm, gerencia_area_id: v })}
                >
                  <SelectTrigger data-testid="edit-gerencia-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {gerencias.map((g) => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-due">Fecha límite</Label>
                <Input
                  id="edit-due"
                  type="datetime-local"
                  value={editForm.due_date}
                  onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleEditSave}
              disabled={actionLoading}
              className="bg-[#7FD02E] hover:bg-[#6BB825] text-white"
              data-testid="save-ticket-btn"
            >
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
